'use client'

import { useState, useContext } from 'react'
import { Dialog } from '@vinted/web-ui'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  createReturnShipment,
  ComplaintModel,
  ReturnShippingMethodPayingSide,
  RequestReturnModalKeys,
} from '@marketplace-web/complaints/complaints-data'
import { AddressFormType } from '@marketplace-web/address/address-data'

import { ShippingContactType } from '@marketplace-web/shipping/shipping-contact-data'

import Intro from './Intro'
import TotalRefundModal from '../TotalRefundModal'
import RequestReturnDetailsContext from '../RequestReturn/RequestReturnProvider/RequestReturnContext'
import ShippingDiscountModal from '../ShippingDiscountModal'
import AddressModal from '../AddressModal'

import ShippingContactModal from '../ShippingContactModal'
import { addressFieldConfiguration } from '../../utils'

type Props = {
  complaint?: ComplaintModel | null
  isBundle?: boolean
  transactionId?: number
  url?: string
  isDataLoading: boolean
  onClose: () => void
  onProceedToCheckout: () => void
  onFetchReturnShipment: () => void
  onConfirm?: () => void
  onUrlNavigation?: () => void
}

const RequestReturnModal = ({
  transactionId,
  isBundle,
  complaint,
  url,
  onClose,
  isDataLoading,
  onProceedToCheckout,
  onFetchReturnShipment,
  onConfirm,
  onUrlNavigation,
}: Props) => {
  const [activeModal, setActiveModal] = useState<RequestReturnModalKeys | null>(
    RequestReturnModalKeys.Intro,
  )
  const [isCreateReturnShipmentLoading, setIsCreateReturnShipmentLoading] = useState(false)

  const { safeBack } = useBrowserNavigation()

  const { requestReturnDetails: returnShipment, selectedPaymentSide } = useContext(
    RequestReturnDetailsContext,
  )

  const proceedToCheckout = selectedPaymentSide === ReturnShippingMethodPayingSide.Seller

  const closeModal = () => {
    setActiveModal(RequestReturnModalKeys.Intro)
  }

  const handleAddressModalOpen = () => {
    setActiveModal(RequestReturnModalKeys.AddressModal)
  }

  const handleShippingContactClick = () => {
    setActiveModal(RequestReturnModalKeys.ShippingContactModal)
  }

  const handleShippingDiscountClick = () => {
    setActiveModal(RequestReturnModalKeys.ShippingDiscountModal)
  }

  const handleTotalRedundInfoClick = () => {
    setActiveModal(RequestReturnModalKeys.TotalRefundModal)
  }

  const handleInfoModalClose = () => {
    setActiveModal(RequestReturnModalKeys.Intro)
  }

  const handleConfirm = async () => {
    onConfirm?.()
    if (proceedToCheckout) {
      onProceedToCheckout()

      return
    }

    if (!transactionId) return

    setIsCreateReturnShipmentLoading(true)

    const response = await createReturnShipment(transactionId)

    setIsCreateReturnShipmentLoading(false)

    if ('errors' in response) {
      return
    }

    if (url) {
      onUrlNavigation?.()
      safeBack(url)

      return
    }

    onClose()
  }

  const handleSubmit = () => {
    onFetchReturnShipment()

    closeModal()
  }

  const renderLoader = () => {
    return <ContentLoader size={ContentLoader.Size.Large} testId="request-return-modal-loader" />
  }

  const renderActiveModal = () => {
    switch (activeModal) {
      case RequestReturnModalKeys.Intro:
        return (
          returnShipment && (
            <Intro
              transactionId={transactionId}
              returnShipment={returnShipment}
              complaint={complaint}
              isBundle={isBundle}
              isLoading={isCreateReturnShipmentLoading}
              onClose={onClose}
              onConfirm={handleConfirm}
              onAddressModalOpen={handleAddressModalOpen}
              onShippingContactClick={handleShippingContactClick}
              onShippingDiscountClick={handleShippingDiscountClick}
              onTotalRefundClick={handleTotalRedundInfoClick}
            />
          )
        )
      case RequestReturnModalKeys.AddressModal:
        return (
          <AddressModal
            isOpen
            address={returnShipment?.userAddress}
            transactionId={transactionId}
            fieldConfiguration={addressFieldConfiguration}
            translationPrefix="conversation.get_shipping_label.modal.address"
            addressFormType={AddressFormType.GenerateLabelAddress}
            isPending={false}
            shippingOrderId={returnShipment?.shippingOrderId}
            onClose={closeModal}
            onSubmit={handleSubmit}
          />
        )
      case RequestReturnModalKeys.ShippingContactModal:
        return (
          transactionId && (
            <ShippingContactModal
              isOpen
              transactionId={transactionId}
              type={ShippingContactType.Seller}
              phoneNumber={returnShipment?.phoneNumber}
              shippingOrderId={returnShipment?.shippingOrderId}
              onClose={handleSubmit}
            />
          )
        )
      case RequestReturnModalKeys.ShippingDiscountModal:
        return (
          <ShippingDiscountModal
            isOpen
            onClose={handleInfoModalClose}
            discount={returnShipment?.pickupPointDetails?.discount}
            lowestPriceIn30Days={returnShipment?.pickupPointDetails?.lowestPriceIn30Days}
          />
        )
      case RequestReturnModalKeys.TotalRefundModal:
        return (
          complaint && (
            <TotalRefundModal
              isOpen
              isBundleOrder={complaint.isBundleOrder}
              onClose={closeModal}
              refundEstimate={complaint.estimatedRelease}
              isOpenedInDialog
            />
          )
        )
      default:
        return null
    }
  }

  if (isDataLoading) return <Dialog show>{renderLoader()}</Dialog>

  return renderActiveModal()
}

export default RequestReturnModal
